/* Verona Tempo r9 (D5/U7/U10): bag drawer + bag page features. Deferred on every page, after vt-theme.js (helpers: window.vtCore). */
/**
 * What this file adds (the bag's own markup is Liquid: sections/cart-drawer.liquid, sections/main-cart.liquid,
 * snippets/cart-item.liquid, snippets/vt-cart-summary.liquid — the order summary and "You saved" line are
 * re-rendered by Symmetry with the bag, so no JS maths is needed for them):
 *  - T5 drawer keyboard: Tab stays inside the open drawer (list of buttons/links worked out on every Tab, so it
 *    also covers lines added or removed while it is open), Escape closes it from anywhere, focus goes back to
 *    what opened it (or the header bag link) and never gets lost when a line disappears.
 *  - T5 screen readers: short polite messages when a quantity changes, a line is added or removed, and the new
 *    subtotal ("Blitz: quantity 2. Subtotal: $50.00."). Undo / change size keep their own messages.
 *  - The "Undo" message (vt-theme.js) is shown inside the open drawer, where it can be seen and reached by
 *    keyboard (it used to sit behind the drawer).
 *  - Space works on the +/− and Remove links (they act as buttons).
 *  - Line photos in the drawer load as soon as it opens (not "lazy").
 * Strings: window.vtConfig.cart (snippets/vt-cart-config.liquid). Everything stays usable without this file.
 */
(function () {
  'use strict';

  var core = window.vtCore || {};
  var bag = (window.vtConfig && window.vtConfig.cart) || {};
  var S = bag.strings || {};
  var tpl = core.tpl || function (s, v) { return String(s || '').replace(/\[(\w+)\]/g, function (m, k) { return v && v[k] !== undefined ? v[k] : m; }); };
  var money = core.formatMoney || function (c) { return '$' + (c / 100).toFixed(2); };
  var announce = core.announce || function () {};
  var route = core.route || function (p) { return '/' + String(p).replace(/^\//, ''); };

  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex^="-"])';

  function isCartPage() { return document.body.classList.contains('template-cart'); }
  function drawerEl() { return document.querySelector('cart-drawer.js-cart-drawer'); }
  function isOpen(d) { return !!(d && d.hasAttribute('open')); }
  function visible(el) {
    if (!el || el.closest('[hidden], [aria-hidden="true"]:not(cart-drawer)')) return false;
    if (!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) return false;
    return getComputedStyle(el).visibility !== 'hidden';
  }
  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), visible);
  }

  /* ---------------------------------------------------------------- T5: drawer keyboard + focus */
  function initDrawerFocus(drawer) {
    if (!drawer || drawer.vtFocusBound) return;
    drawer.vtFocusBound = true;
    var fallback = null;
    var openedAt = 0;

    drawer.addEventListener('on:cart-drawer:before-open', function () {
      var a = document.activeElement;
      fallback = a && a !== document.body && !drawer.contains(a) ? a : null;
      openedAt = Date.now();
      drawer.vtOpenedAt = openedAt;
    });

    // SideDrawer.close() gives focus back to drawer.opener; make sure that is something real and still on the page
    drawer.addEventListener('on:cart-drawer:before-close', function () {
      var target = drawer.opener;
      if (!target || !target.isConnected || drawer.contains(target) || !visible(target)) {
        target = fallback && fallback.isConnected && visible(fallback) ? fallback : null;
      }
      if (!target) {
        target = Array.prototype.filter.call(document.querySelectorAll('.cart-link, [data-vt-bnav-action="cart"], a[href$="/cart"]'), function (el) {
          return !drawer.contains(el) && visible(el);
        })[0] || null;
      }
      drawer.opener = target;
    });

    // Tab / Shift+Tab wrap inside the drawer, worked out on every key press (the bag's content changes while open)
    drawer.addEventListener('keydown', function (evt) {
      if (evt.key !== 'Tab' || !isOpen(drawer)) return;
      var list = focusables(drawer);
      if (!list.length) { evt.preventDefault(); drawer.focus(); evt.stopPropagation(); return; }
      var first = list[0];
      var last = list[list.length - 1];
      var active = document.activeElement;
      if (evt.shiftKey && (active === first || active === drawer || !drawer.contains(active))) {
        evt.preventDefault();
        evt.stopPropagation();
        last.focus();
      } else if (!evt.shiftKey && (active === last || !drawer.contains(active))) {
        evt.preventDefault();
        evt.stopPropagation();
        first.focus();
      }
    }, true);

    // Escape closes the drawer even when focus is not inside it (e.g. after a line was removed)
    document.addEventListener('keydown', function (evt) {
      if (evt.key !== 'Escape' || evt.defaultPrevented || !isOpen(drawer)) return;
      if (document.querySelector('dialog[open]')) return; // size guide or another dialog on top
      if (evt.target && evt.target.closest && evt.target.closest('.cart-drawer-popup')) return;
      if (typeof drawer.close === 'function') drawer.close();
    });

    // focus lost (the focused line was removed, or focus fell outside) → back into the drawer
    var rescue = function () {
      if (!isOpen(drawer)) return;
      var a = document.activeElement;
      if (a && a !== document.body && a.isConnected) return;
      // a line was just removed: its "Undo" button is the natural next step, otherwise the drawer itself
      var undo = drawer.querySelector('.vt-undo--in-bag .vt-undo__btn:not([disabled]):not([hidden])');
      if (undo) undo.focus(); else drawer.focus();
    };
    drawer.addEventListener('on:cart:after-merge', function () {
      window.setTimeout(rescue, 60);
      window.setTimeout(rescue, 1000); // removed lines fade out first, then leave the page
    });
  }

  /* ---------------------------------------------------------------- Undo message inside the open drawer */
  function initUndoInDrawer(drawer) {
    if (!drawer || !('MutationObserver' in window)) return;
    var mo = new MutationObserver(function (records) {
      // new look only: in the old look the message keeps its own fixed place (its styles are not made for the drawer)
      if (!isOpen(drawer) || !drawer.classList.contains('vt-bag')) return;
      records.forEach(function (r) {
        Array.prototype.forEach.call(r.addedNodes, function (n) {
          if (n.nodeType !== 1 || !n.classList.contains('vt-undo')) return;
          var list = drawer.querySelector('.cart-drawer__content .cart-item-list');
          if (!list || !list.parentNode) return;
          n.classList.add('vt-undo--in-bag');
          list.parentNode.insertBefore(n, list);
        });
      });
    });
    mo.observe(document.body, { childList: true });
  }

  /* ---------------------------------------------------------------- line photos show at once */
  // The drawer's photos only start loading when it opens (Symmetry "manual" images); as "lazy" images they then
  // waited for another layout pass. Make them load straight away once the drawer asks for them.
  function initEagerPhotos(drawer) {
    if (!drawer) return;
    var eager = function () {
      drawer.querySelectorAll('.cart-item__image img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
    };
    drawer.addEventListener('on:cart-drawer:before-open', eager);
    drawer.addEventListener('on:cart:after-merge', eager);
  }

  /* ---------------------------------------------------------------- Space on the link-buttons */
  function initLinkButtons() {
    document.addEventListener('keydown', function (evt) {
      if (evt.key !== ' ' && evt.key !== 'Spacebar') return;
      var el = evt.target;
      if (!el || !el.matches || !el.matches('a[role="button"]')) return;
      if (!el.closest('.cart-item')) return;
      evt.preventDefault();
      el.click();
    });
  }

  /* ---------------------------------------------------------------- T5: polite bag messages */
  function initAnnouncements(drawer) {
    if (!core.onCart) return;
    var prev = null;
    var undoAt = 0;
    var swapAt = 0;
    document.addEventListener('click', function (evt) {
      if (!evt.target.closest) return;
      if (evt.target.closest('.vt-undo__btn')) undoAt = Date.now();
    }, true);
    document.addEventListener('change', function (evt) {
      if (evt.target.closest && evt.target.closest('vt-line-variant')) swapAt = Date.now();
    }, true);

    var snapshot = function (cart) {
      var map = {};
      (cart.items || []).forEach(function (it) { map[it.key] = { qty: it.quantity, title: it.product_title || it.title, pid: it.product_id }; });
      return { map: map, count: cart.item_count, total: cart.total_price };
    };
    var baseline = function () {
      if (prev) return;
      fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart && !prev) prev = snapshot(cart); })
        .catch(function () {});
    };
    if (isCartPage()) baseline();
    if (drawer) drawer.addEventListener('on:cart-drawer:before-open', baseline);

    core.onCart(function (cart) {
      var now = snapshot(cart);
      var before = prev;
      prev = now;
      if (!before) return;
      var inView = isCartPage() || (isOpen(drawer) && Date.now() - (drawer.vtOpenedAt || 0) > 1500);
      if (!inView) return;
      var parts = [];
      var added = [];
      var removed = [];
      Object.keys(now.map).forEach(function (k) {
        var b = before.map[k];
        var n = now.map[k];
        if (!b) added.push(n);
        else if (b.qty !== n.qty) parts.push(tpl(S.qtyChanged, { title: n.title, qty: n.qty }));
      });
      Object.keys(before.map).forEach(function (k) { if (!now.map[k]) removed.push(before.map[k]); });
      var recent = function (t) { return Date.now() - t < 8000; };
      // a size/colour swap (its own "Your bag was updated" message) or an undo (its own "... is back") — say only the total
      var swapped = recent(swapAt) && added.length === 1 && removed.length === 1 && added[0].pid === removed[0].pid;
      if (!swapped && !recent(undoAt)) {
        added.forEach(function (n) { parts.push(tpl(S.added, { title: n.title })); });
      }
      if (!swapped && !document.querySelector('.vt-undo')) {
        removed.forEach(function (b) { parts.push(tpl(S.removed, { title: b.title })); });
      }
      if (!parts.length && before.total === now.total && before.count === now.count) return;
      parts.push(now.count ? tpl(S.subtotalNow, { amount: money(now.total) }) : S.emptyNow);
      announce(parts.filter(Boolean).join(' '));
    });
  }

  function init() {
    var drawer = drawerEl();
    initDrawerFocus(drawer);
    initUndoInDrawer(drawer);
    initEagerPhotos(drawer);
    initLinkButtons();
    initAnnouncements(drawer);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();


/* 7 Oct 2026 (ads team: "the bag says 'Your bag is empty' after the form is closed, until a reload"): whenever the
   page comes back (back button / tab), the bag drawer opens or the Cash on Delivery form closes, the real bag
   (/cart.js) is compared with the count in the header; if they differ the bag, header and bag page redraw
   themselves (the theme's own on:cart:change) — no reload needed. */
(function () {
  'use strict';
  if (window.vtBagSync) return;
  window.vtBagSync = true;
  var busy = false;
  function shown() {
    var c = document.querySelector('.cart-link__count');
    var n = c ? parseInt((c.textContent || '').replace(/[^0-9]/g, ''), 10) : 0;
    return isNaN(n) ? 0 : n;
  }
  function check() {
    if (busy) return;
    busy = true;
    fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        busy = false;
        var emptyShown = !!document.querySelector('.cart-drawer__empty-content:not(.cart-drawer__empty-content--hidden)');
        if (cart.item_count !== shown() || (cart.item_count > 0 && emptyShown)) {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
        }
      })
      .catch(function () { busy = false; });
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) check(); });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') check(); });
  var drawer = document.querySelector('cart-drawer, .cart-drawer');
  if (drawer) drawer.addEventListener('on:cart-drawer:before-open', check);
  // the Cash on Delivery form (EasySell) closing
  var wasOpen = false;
  setInterval(function () {
    var m = document.getElementById('easysell-modal');
    var d = m && m.style.display; // EasySell's inline display style (cheap; no page style work)
    var open = !!(d && d !== 'none');
    if (wasOpen && !open) setTimeout(check, 400);
    wasOpen = open;
  }, 700);
})();
